import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { ChevronDown, Phone, MessageCircle, Mail } from "lucide-react";
import { LeadFormModal } from "@/components/LeadFormModal";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { speakableSchema, BUILD_DATE } from "@/lib/aeo";
import { RelatedHubGrid } from "@/components/RelatedHubGrid";

const SITE = "https://nyclanguageinstitute.com";
const TITLE = "FAQ — Translation, Notary, Legalization, Visa in Thailand | NYC Language Institute";
const DESC =
  "Answers to 30+ common questions on certified translation, Notary Public, MFA/embassy legalization, NAATI. Send your documents for a free eligibility check.";

type FAQ = { q: string; a: string };
type Group = { name: string; faqs: FAQ[] };

const GROUPS: Group[] = [
  {
    name: "Certified Translation",
    faqs: [
      { q: "How many languages do you translate?", a: "Over 50 languages handled in-house by native-fluent linguists. Headline rates: English ⇄ Thai from ฿500 per A4 page, Asian scripts (Japanese, Korean, Chinese, Vietnamese) ฿700, European (French, German, Spanish, Italian) ฿800, and rare pairs (Arabic, Russian, Hebrew) from ฿1,200. Every job ships with a signed certificate of accuracy." },
      { q: "What is the turnaround time?", a: "Standard delivery is 1–3 Thai business days for documents up to 5 pages. Same-day, 4-hour, and overnight rush are available with a 50% surcharge. A locked quote returns within 15 minutes during business hours (Mon–Sat 09:00–18:00 ICT) via LINE @nycli or 083-249-4999." },
      { q: "Who accepts your translations?", a: "Thai Ministry of Foreign Affairs (MFA), 40+ embassies in Bangkok, Thai courts, Department of Lands, BOI, Australian NAATI (CPN9X0Q4P), USCIS, UKVI, Canadian IRCC, and German/French/Italian missions. Every translator is registered with the Thai Ministry of Justice and we are ISO 17100-aligned." },
      { q: "Do you handle medical, legal, and financial documents?", a: "Yes — dedicated specialists in each domain. Medical files are translated by linguists with MD/RN backgrounds, legal contracts by Thai-licensed lawyers, financial audits by CPAs, and technical patents by registered engineers. Confidentiality NDA signed on request and HIPAA-grade handling for medical records." },
      { q: "Is there a quality guarantee?", a: "100% acceptance guarantee. Free corrections for the life of the document. If a translation is rejected by MFA, an embassy, USCIS, IRCC, UKVI, or any government body for translation-quality reasons, we cover MFA re-legalization fees and the cost of re-submission — recorded in writing in your invoice." },
    ],
  },
  {
    name: "Notary Public",
    faqs: [
      { q: "Is a Thai Notary Public valid abroad?", a: "Yes. Notarial Services Attorneys registered with the Lawyers Council of Thailand (under section 17 of the Lawyers Act B.E. 2528) are authorised to certify signatures, copies, and affidavits for international use — equivalent in effect to a Notary Public in Common Law jurisdictions, and routinely accepted by US, UK, Australian, and EU authorities after MFA legalisation." },
      { q: "How many in-house Notary attorneys?", a: "Six in-house Notarial Services Attorneys, all registered with the Lawyers Council of Thailand. Registration numbers, photo IDs, and Council certificates are verifiable on request and listed on our Trust page. Coverage Monday to Saturday with same-day appointments via LINE @nycli or 083-249-4999." },
      { q: "What does Notary certification cost?", a: "Single notarial certificate from ฿800. Affidavit drafting and notarisation ฿1,500. Power of Attorney drafting + notarisation ฿3,000. Corporate documentary package (board minutes, share register, certified copies) from ฿5,000. Bundle pricing with MFA legalisation and embassy walk-through available — quoted within 15 minutes." },
      { q: "How fast is Notary service?", a: "Same-day in the vast majority of cases — typical wait at our Asoke office is 30 minutes to 2 hours. Pre-booked appointments via LINE @nycli skip the queue. After-hours and weekend slots available by arrangement for time-critical filings such as overseas property closings." },
    ],
  },
  {
    name: "MFA & Embassy Legalization",
    faqs: [
      { q: "How long for MFA legalization?", a: "Standard MFA Department of Consular Affairs legalisation runs 3–5 Thai business days. Same-day express is available for an additional ฿1,200 per document. We submit, queue, courier, track, and return — you never need to visit Chaeng Watthana. Real-time status updates pushed via LINE @nycli." },
      { q: "MFA legalization price?", a: "฿800 per document standard, ฿2,000 same-day express. Combo (certified translation + MFA legalisation + embassy lodgement) starts from ฿2,500 per document. Bulk discounts from 10 documents. All prices VAT-inclusive and issued on a Thai Revenue Department e-Tax Invoice." },
      { q: "Which embassies do you cover?", a: "40+ embassies and consulates in Bangkok: China, Japan, Korea, USA, UK, Germany, France, Italy, Russia, Saudi Arabia, UAE, Qatar, India, Vietnam, Indonesia, Philippines, Malaysia, Singapore, Australia, New Zealand, Canada, Brazil, Switzerland, Netherlands, Spain, Sweden, Norway, Denmark, Finland, plus the Hague Apostille route via MFA since 14 December 2024." },
      { q: "Do I need to visit the embassy myself?", a: "No. Our courier and case-handler team manage every step — appointment booking, document lodgement, follow-up calls, and secure return delivery. The only exceptions are embassies that legally require personal biometrics (US, UK, Schengen), where we still prepare every form and escort you to the counter." },
    ],
  },
  {
    name: "NAATI Australia",
    faqs: [
      { q: "What is NAATI?", a: "The National Accreditation Authority for Translators and Interpreters — Australia's only government-recognised certifying body for the language industry. NAATI-certified translations are accepted by the Department of Home Affairs (DIBP), AAT, Australian courts, every state RTO, all 43 universities, and consular missions. Required for any document not originally in English on Australian visa files." },
      { q: "Do you hold a NAATI number?", a: "Yes — NYC Language Institute holds active NAATI practitioner number CPN9X0Q4P, certified in the Thai ⇄ English direction. The number is independently verifiable on the public register at naati.com.au and appears as a digital stamp on every translation we ship for Australian use." },
      { q: "Which Australian visas can use NAATI translations?", a: "All categories without exception — Skilled Migration (189, 190, 491), Partner (309/100, 820/801), Prospective Marriage (300), Student (500), Subclass 482/186/494 employer-sponsored, Tourist (600), Working Holiday (417/462), Parent (103/143), Resident Return (155), and Australian Citizenship by descent or conferral." },
      { q: "NAATI translation price?", a: "From ฿1,300 per page — typically 60–70% cheaper than translating the same document inside Australia, with identical legal acceptance. Standard turnaround 2 business days, same-day rush available. Documents shipped as digital PDF with NAATI stamp plus hard-copy delivery via DHL on request." },
    ],
  },
  {
    name: "Visa & Work Permit",
    faqs: [
      { q: "Which visas do you handle?", a: "Thai inbound: Non-B (Work), Work Permit, LTR, Smart Visa, Thailand Privilege (Elite), DTV (Digital Nomad), Marriage (O), Retirement (O-A/O-X), Education (ED), Permanent Residence. Thai outbound: Australian (189/190/491/500/600), UK (Visitor/Skilled Worker/Student), US (B1/B2/F1/H1B), Canadian (Visitor/Study/Work), and full Schengen coverage across 27 member states." },
      { q: "What is your approval rate?", a: "We do not publish an approval-rate figure: the decision rests entirely with the embassy or receiving authority, and any number we quoted could not be independently verified. What we control is process - every document is pre-screened against the receiving authority current checklist, we run a mock interview before consular appointments, and we tell you honestly when a case is weak before we take it on." },
      { q: "Visa service fees?", a: "Depends on category — Tourist / Non-O from ฿8,000, Non-B + Work Permit bundle from ฿35,000, LTR from ฿45,000, DTV from ฿18,000, Thailand Privilege concierge from ฿25,000 on top of the government fee. Free 30-minute initial consultation by phone, LINE, or Zoom before any commitment." },
      { q: "Money-back guarantee?", a: "Cases we pre-approve after our written eligibility assessment carry a partial refund guarantee — the exact percentage is fixed in your engagement letter before payment, never verbal. Government and embassy fees, which we never touch, are non-refundable. Full terms reviewed during the free consultation." },
    ],
  },
  {
    name: "Payment & Process",
    faqs: [
      { q: "Payment methods?", a: "Bank transfer (BBL/SCB/KBank/Krungsri), PromptPay QR, Visa/Mastercard/JCB/AMEX via PCI-DSS Level-1 gateway (2C2P), and corporate wire in USD/EUR/SGD/GBP/AUD/JPY. Foreign clients receive a tax-compliant invoice quoting Thai TIN 0105556023896 and SWIFT details on request; cryptocurrency is not accepted under BoT 2024 guidance." },

      { q: "Is a deposit required?", a: "Small jobs (<฿5,000) paid on completion. Larger jobs / Combo packages: 50% deposit. Net-30 credit terms available for verified companies." },
      { q: "Do you issue tax invoices?", a: "Yes — every invoice is a fully compliant e-Tax Invoice & e-Receipt issued under Thai Revenue Department Notification 31/2560 (RD §86/4). It carries our TIN 0105556023896, a digital signature with timestamp from the RD-approved gateway, and is delivered simultaneously by email (PDF/A-3) and Thai Post for accounting and VAT-refund purposes." },
      { q: "How are documents returned?", a: "Bangkok metro: same-day delivery 09:00–20:00 by our in-house courier across all 50 districts. Up-country Thailand: Kerry Express / Thailand Post EMS next-day with tracking. International: DHL Express to 220 countries (1–4 business days), FedEx Priority backup, registered air-mail for archival use. Every shipment carries an insured tracking number and signed proof-of-delivery." },
    ],
  },
];

export const Route = createFileRoute("/en/faq")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, "/en/faq", "en")},
      { name: "description", content: ctaDesc(DESC, "/en/faq", "en")},
      { property: "og:title", content: ctaTitle(TITLE, "/en/faq", "en")},
      { property: "og:description", content: ctaDesc(DESC, "/en/faq", "en")},
      { property: "og:url", content: `${SITE}/en/faq` },
      { property: "og:locale", content: "en_US" },
    ],
    links: [
      { rel: "canonical", href: `${SITE}/en/faq` },
      ...hreflangLinks("/faq", "/en/faq"),
    ],
    scripts: [
      speakableSchema([".aeo-speakable", "h1", "summary"]),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${SITE}/en/faq#faqpage`,
          url: `${SITE}/en/faq`,
          name: TITLE,
          description: DESC,
          inLanguage: "en",
          dateModified: BUILD_DATE,
          isPartOf: { "@id": `${SITE}#website` },
          about: { "@id": `${SITE}#org` },
          author: { "@type": "Organization", "@id": `${SITE}#org`, name: "NYC Language Institute", url: SITE },
          publisher: { "@id": `${SITE}#org` },
          mainEntity: GROUPS.flatMap((g) => g.faqs).map((f, i) => ({
            "@type": "Question",
            "@id": `${SITE}/en/faq#q-${i + 1}`,
            name: f.q,
            answerCount: 1,
            acceptedAnswer: {
              "@type": "Answer",
              text: f.a,
              inLanguage: "en",
              author: { "@type": "Organization", "@id": `${SITE}#org` },
              upvoteCount: 1,
              url: `${SITE}/en/faq#q-${i + 1}`,
            },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/en` },
            { "@type": "ListItem", position: 2, name: "FAQ", item: `${SITE}/en/faq` },
          ],
        }),
      },
    ],
  }),
  component: EnFaqPage,
});

function EnFaqPage() {
  return (
    <main className="min-h-screen bg-navy-deep text-white">
      <section className="border-b border-white/10 bg-gradient-to-b from-[#0a1a3d] to-[#0d2150] px-4 py-14">
        <div className="mx-auto max-w-4xl">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <Link to="/en" className="text-amber-300 hover:underline">← EN home</Link>
            <span className="text-white/30">·</span>
            <Link to="/faq" className="text-white/80 hover:text-amber-300">ภาษาไทย</Link>
          </div>
          <h1 className="aeo-speakable mt-4 text-3xl font-extrabold sm:text-5xl">
            Frequently Asked <span className="bg-gradient-to-r from-amber-300 to-amber-500 bg-clip-text text-transparent">Questions</span>
          </h1>
          <p className="mt-4 max-w-2xl text-white/80">
            Quick answers on price, turnaround, accepted documents, and acceptance authorities.
          </p>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto max-w-4xl space-y-8">
          {GROUPS.map((g) => (
            <div key={g.name}>
              <h2 className="text-lg font-bold text-amber-200">{g.name}</h2>
              <div className="mt-3 space-y-2">
                {g.faqs.map((f) => <FaqItem key={f.q} q={f.q} a={f.a} />)}
              </div>
            </div>
          ))}

          <div className="flex flex-wrap items-center justify-center gap-3 rounded-2xl border border-amber-400/30 bg-amber-500/5 p-6">
            <a href="tel:+66832494999" data-track-zone="en-faq-footer" className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-3 text-sm font-bold text-navy-deep hover:bg-amber-400">
              <Phone className="h-4 w-4" /> +66 83 249 4999
            </a>
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" data-track-zone="en-faq-footer" className="inline-flex items-center gap-2 rounded-xl bg-[#04A03A] px-5 py-3 text-sm font-bold text-white hover:bg-[#05B14C]">
              <MessageCircle className="h-4 w-4" /> LINE @nycli
            </a>
            <LeadFormModal trigger={
              <button type="button" data-track-zone="en-faq-footer" className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/5 px-5 py-3 text-sm font-bold text-white hover:bg-white/10">
                <Mail className="h-4 w-4" /> Ask a custom question
              </button>
            } />
          </div>
        </div>
      </section>
    </main>
  );
}

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <details
      open={open}
      onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}
      className="group rounded-xl border border-white/10 bg-white/[0.03] open:bg-white/[0.05]"
    >
      <summary className="flex cursor-pointer items-center justify-between gap-2 px-5 py-3 text-sm font-semibold text-white/90 list-none">
        <span>{q}</span>
        <ChevronDown className={`h-4 w-4 flex-none text-amber-300 transition ${open ? "rotate-180" : ""}`} />
      </summary>
      <div className="border-t border-white/10 px-5 py-3 text-sm text-white/75">{a}<RelatedHubGrid locale="en" />
      </div>
    </details>
  );
}
